import { Check } from "lucide-react";

/**
 * A trilha de passos da jornada, na linguagem bento (`bento/ui.tsx`).
 *
 * Antes eram pastilhas soltas separadas por travessão — davam a informação, mas
 * não davam a SENSAÇÃO de progresso, que é o que faz alguém terminar um
 * cadastro. Aqui o passo andado fica marcado com o tique, o atual ganha o
 * círculo cheio da marca, e a linha entre eles preenche até onde se chegou.
 *
 * No celular a trilha inteira não cabe sem virar letrinha ilegível, então ela
 * troca de forma: barra de progresso + "Passo 2 de 5 · Perfil". Mesma
 * informação, do tamanho que cabe.
 */
export interface PassoDaTrilha {
  id: string;
  rotulo: string;
}

export const PassosBento = ({
  passos,
  indice,
  aoEscolher,
}: {
  passos: PassoDaTrilha[];
  indice: number;
  /** Com ele, cada passo vira botão e dá para pular direto (a edição usa). */
  aoEscolher?: (indice: number) => void;
}) => {
  const pct = passos.length > 1 ? (indice / (passos.length - 1)) * 100 : 0;

  return (
    <>
      {/* Celular: barra + nome do passo */}
      <div className="sm:hidden">
        <div className="flex items-baseline justify-between">
          <p className="text-[12.5px] font-semibold text-[#6b7480] dark:text-muted-foreground">
            Passo {indice + 1} de {passos.length}
          </p>
          <p className="text-[13px] font-semibold">{passos[indice]?.rotulo}</p>
        </div>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[rgba(53,70,96,0.1)]">
          <div
            className="h-full rounded-full bg-ouro transition-[width] duration-300"
            style={{ width: `${((indice + 1) / passos.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Telas maiores: a trilha inteira */}
      <div className="relative hidden sm:block">
        {/* Trilho e preenchimento ficam atrás dos círculos, centrados neles. */}
        <div className="absolute left-0 right-0 top-[15px] h-[2px] bg-[rgba(53,70,96,0.12)]" />
        <div
          className="absolute left-0 top-[15px] h-[2px] bg-ouro transition-[width] duration-300"
          style={{ width: `${pct}%` }}
        />
        <ol className="relative flex justify-between">
          {passos.map((passo, i) => {
            const feito = i < indice;
            const atual = i === indice;
            const conteudo = (
              <>
                <span
                  aria-current={atual ? "step" : undefined}
                  className={`flex h-8 w-8 items-center justify-center rounded-full border-2 text-[12.5px] font-bold tabular-nums transition-colors ${
                    feito
                      ? "border-ouro bg-ouro text-marinho"
                      : atual
                        ? "border-marinho bg-marinho text-white shadow-[0_0_0_4px_rgba(196,173,142,0.25)]"
                        : "border-[rgba(53,70,96,0.15)] bg-card text-muted-foreground"
                  }`}
                >
                  {feito ? <Check className="h-4 w-4" /> : i + 1}
                </span>
                <span
                  className={`text-[12.5px] ${
                    atual
                      ? "font-semibold text-marinho"
                      : "font-medium text-muted-foreground dark:text-muted-foreground"
                  }`}
                >
                  {passo.rotulo}
                </span>
              </>
            );
            return (
              <li key={passo.id}>
                {aoEscolher ? (
                  <button
                    type="button"
                    onClick={() => aoEscolher(i)}
                    className="flex cursor-pointer flex-col items-center gap-1.5 rounded-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                  >
                    {conteudo}
                  </button>
                ) : (
                  <div className="flex flex-col items-center gap-1.5">{conteudo}</div>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </>
  );
};
